import { type MenuProps, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import type { SortOrder, SorterResult } from "antd/es/table/interface"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RowActionsMenu } from "../../../components/EntityTable/RowActionsMenu.tsx"
import Table from "../../../components/Table"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import {
  DEFAULT_OVERVIEW_SORT,
  type OverviewSort,
  type OverviewSortColumn,
  compareBy,
  describeCategory,
  describeMessage,
  describeRepeat,
  describeSends,
  describeTiming,
  describeTrigger,
  listStatus
} from "../helpers/automation-list"
import type { Automation } from "../types/automation.types"
import { StatusBadge } from "./builder/StatusBadge"

const { Text } = Typography

const SORT_CYCLE: SortOrder[] = ["ascend", "descend", "ascend"]

const INTERACTIVE = "a, button, [role=menu], [role=menuitem]"

interface AutomationsTableProps {
  rows: Automation[]
  canManage: boolean
  /** Rows whose pause/enable request is still in flight. */
  togglingIds?: ReadonlySet<string>
  onEdit: (automation: Automation) => void
  onDuplicate: (automation: Automation) => void
  onToggle: (automation: Automation, next: boolean) => void
  onDelete: (automation: Automation) => void
  onHistory: (automation: Automation) => void
  /** Controlled; the rows are sorted on the client. */
  sort?: OverviewSort
  /** A header click; `null` when the click clears the sort. */
  onSortChange?: (sort: OverviewSort | null) => void
}

export function AutomationsTable({
  rows,
  canManage,
  togglingIds,
  onEdit,
  onDuplicate,
  onToggle,
  onDelete,
  onHistory,
  sort = DEFAULT_OVERVIEW_SORT,
  onSortChange
}: AutomationsTableProps) {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  // A header only ever flips between the two orders: the sort never clears,
  // so the default Last saved (descending) can still go ascending.
  const comparators = compareBy(t)
  const sortable = (column: OverviewSortColumn) => ({
    sorter: comparators[column],
    sortDirections: SORT_CYCLE,
    sortOrder: sort.column === column ? sort.order : null
  })
  const openInNewTab = (record: Automation) =>
    window.open(RoutesPaths.settings.automationEdit(record.id), "_blank", "noopener")

  const menuItems = (record: Automation): MenuProps["items"] => {
    const history = {
      key: "history",
      label: t("settingsAutomation.table.menu.history"),
      onClick: () => onHistory(record)
    }
    if (!canManage) return [history]
    return [
      { key: "edit", label: t("settingsAutomation.table.menu.edit"), onClick: () => onEdit(record) },
      {
        key: "duplicate",
        label: t("settingsAutomation.table.menu.duplicate"),
        onClick: () => onDuplicate(record)
      },
      history,
      {
        key: "toggle",
        label: t(`settingsAutomation.table.menu.${record.is_enabled ? "pause" : "enable"}`),
        disabled: !!togglingIds?.has(record.id),
        onClick: () => onToggle(record, !record.is_enabled)
      },
      { type: "divider" },
      {
        key: "delete",
        label: t("settingsAutomation.table.menu.delete"),
        danger: true,
        onClick: () => onDelete(record)
      }
    ]
  }

  const columns: ColumnsType<Automation> = [
    {
      key: "automation",
      title: t("settingsAutomation.table.columns.automation"),
      width: 240,
      render: (_, record) => (
        <div className={"flex min-w-0 flex-col"}>
          {canManage ? (
            <Link
              to={RoutesPaths.settings.automationEdit(record.id)}
              className={"font-semibold"}
              onClick={(event) => event.stopPropagation()}
            >
              {record.name}
            </Link>
          ) : (
            <Text strong>{record.name}</Text>
          )}
          <Text type={"secondary"} className={"text-xs"}>
            {describeSends(t, record)}
          </Text>
        </div>
      )
    },
    {
      key: "category",
      title: t("settingsAutomation.table.columns.category"),
      align: "center",
      width: 120,
      ...sortable("category"),
      render: (_, record) => describeCategory(t, record)
    },
    {
      key: "trigger",
      title: t("settingsAutomation.table.columns.trigger"),
      align: "center",
      width: 160,
      render: (_, record) => describeTrigger(t, record)
    },
    {
      key: "timing",
      title: t("settingsAutomation.table.columns.timing"),
      align: "center",
      width: 170,
      render: (_, record) => {
        const repeat = describeRepeat(t, record)
        return (
          <div className={"flex min-w-0 flex-col"}>
            <span>{describeTiming(t, record)}</span>
            {repeat && (
              <Text type={"secondary"} className={"text-xs"}>
                {repeat}
              </Text>
            )}
          </div>
        )
      }
    },
    {
      key: "message",
      title: t("settingsAutomation.table.columns.message"),
      align: "center",
      width: 170,
      render: (_, record) =>
        record.template_name ? (
          describeMessage(t, record)
        ) : (
          <Text type={"secondary"}>{describeMessage(t, record)}</Text>
        )
    },
    {
      key: "sender",
      title: t("settingsAutomation.table.columns.sender"),
      align: "center",
      width: 200,
      responsive: ["lg"],
      render: (_, record) =>
        record.sender_name ? (
          <div className={"flex min-w-0 flex-col"}>
            <Text>{record.sender_name}</Text>
            <Text type={"secondary"} className={"text-xs"}>
              {record.sender_email}
            </Text>
          </div>
        ) : (
          <Text type={"secondary"}>—</Text>
        )
    },
    {
      key: "status",
      title: t("settingsAutomation.table.columns.status"),
      align: "center",
      width: 110,
      ...sortable("status"),
      render: (_, record) => <StatusBadge status={listStatus(record)} />
    },
    {
      key: "updated",
      title: t("settingsAutomation.table.columns.lastSaved"),
      align: "center",
      width: 150,
      ...sortable("updated"),
      render: (_, record) => formatDate(record.updated)
    },
    {
      key: "actions",
      title: <span className={"sr-only"}>{t("settingsAutomation.table.columns.actions")}</span>,
      width: 64,
      align: "center",
      render: (_, record) => (
        // The menu is portalled, but its clicks (a disabled item's included)
        // still bubble through React to the row, which opens the builder.
        <span onClick={(event) => event.stopPropagation()}>
          <RowActionsMenu
            items={menuItems(record)}
            buttonProps={{
              type: "text",
              "aria-label": t("settingsAutomation.table.actionsFor", { name: record.name })
            }}
          />
        </span>
      )
    }
  ]

  return (
    <Table<Automation>
      rowKey={"id"}
      columns={columns}
      dataSource={rows}
      pagination={false}
      withOutPagination
      scroll={{ x: 1150 }}
      onChange={(_pagination, _filters, sorter) => {
        const { columnKey, order } = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterResult<Automation>
        onSortChange?.(order ? { column: columnKey as OverviewSortColumn, order } : null)
      }}
      onRow={(record) =>
        canManage
          ? {
              onClick: (event) => {
                // A modified click means "somewhere else", as it would on a link.
                if (event.ctrlKey || event.metaKey || event.shiftKey) openInNewTab(record)
                else onEdit(record)
              },
              onAuxClick: (event) => {
                // The link and the actions button (and its portalled menu)
                // answer a middle-click themselves.
                const target = event.target as Element
                if (event.button !== 1 || target.closest(INTERACTIVE)) return
                openInNewTab(record)
              },
              className: "cursor-pointer"
            }
          : {}
      }
    />
  )
}

export type { AutomationsTableProps }
